<template>
  <Teleport to="body">
    <div v-if="modelValue" class="chat-panel-overlay" :style="{ zIndex: zIndexEfetivo }">
      <div class="chat-panel shadow-lg"
           :style="{ '--chat-height': chatHeight + 'px', '--chat-width': chatWidth + 'px' }">
        <!-- Alça de redimensionamento (apenas desktop) -->
        <div
          v-if="!isMobile"
          class="chat-resize-handle"
          @mousedown.prevent="iniciarRedimensionamento"
        ></div>
        <!-- Header -->
        <header class="chat-panel-header">
          <div class="d-flex align-items-center gap-2">
            <h6 class="mb-0">
              <i class="bi bi-robot me-2"></i>
              {{ modoDuvida ? 'Tira Dúvidas' : 'Helper' }}
              <span class="chat-header-badge-ai ms-1">AI</span>
            </h6>
            <span v-if="usuarioLogado?.adminInterno" class="badge badge-admin-interno-chat ms-1">CTech</span>
          </div>
          <button type="button" class="btn-close btn-close-white" @click="fechar" aria-label="Close"></button>
        </header>

        <!-- Banner de perfil público -->
        <div v-if="mostrarBannerPerfil" class="chat-profile-banner">
          <i class="bi bi-info-circle me-2"></i>
          Configure seu <a href="#" @click.prevent="abrirConfigPerfil">perfil público</a> para aparecer com nome nas sugestões.
        </div>

        <!-- Mensagens -->
        <div class="chat-messages" ref="chatContainer">
          <div v-for="(msg, idx) in historico" :key="idx"
               :class="['d-flex mb-2', msg.role === 'user' ? 'justify-content-end' : 'justify-content-start']">
            <div :class="['chat-bubble', msg.role === 'user' ? 'chat-bubble-user' : 'chat-bubble-assistant', msg.role === 'system' ? 'chat-bubble-system' : '']">
              <div class="small" v-html="renderizarMarkdown(msg.content)"></div>
            </div>
          </div>

          <!-- Indicador de digitação -->
          <div v-if="iaPensando" class="d-flex justify-content-start mb-2">
            <div class="chat-bubble chat-bubble-assistant">
              <div class="typing-indicator">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>

          <!-- Caixa de sucesso pós-salvamento -->
          <div v-if="salvoComSucesso" class="px-2 mb-3">
            <div class="card border-success bg-success-subtle">
              <div class="card-body text-center py-4">
                <i class="bi bi-check-circle-fill text-success" style="font-size: 3rem;"></i>
                <h5 class="text-success mt-2 mb-1">Sugestão salva com êxito!</h5>
                <p v-if="contagemRegressiva > 0" class="mb-0 text-muted small">
                  Você será redirecionado para a página de Sugestões em {{ contagemRegressiva }}s...
                </p>
                <button v-else class="btn btn-outline-success mt-2" @click="abrirPaginaSugestoes">
                  <i class="bi bi-box-arrow-up-right me-1"></i>
                  Ir para Sugestões
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer com input -->
        <div class="chat-panel-footer">
          <!-- Badges de opções rápidas (vindos do backend via badge agent) -->
          <div v-if="badgesOpcoes.length > 0 && !finalizado" class="badges-container mb-2">
            <button v-for="opcao in badgesOpcoes" :key="opcao.value" type="button"
                    class="badge badge-opcao me-1 mb-1" @click="selecionarOpcao(opcao.value)">
              {{ opcao.text }}
            </button>
          </div>
          <div class="input-area">
            <textarea class="form-control chat-input" v-model="mensagem"
                      :placeholder="modoDuvida ? 'Digite sua dúvida sobre esta tela...' : 'Descreva sua ideia de melhoria...'"
                      @keydown.enter.prevent="enviarMensagem"
                      :disabled="isLoading || finalizado || etapaSomenteBadges || salvoComSucesso || chatBloqueado"
                      rows="2"></textarea>
            <button class="btn btn-primary btn-send" @click="enviarMensagem"
                    :disabled="isLoading || !mensagem.trim() || finalizado || etapaSomenteBadges || salvoComSucesso || chatBloqueado">
              <span v-if="isLoading" class="spinner-border spinner-border-sm" role="status"></span>
              <i v-else class="bi bi-send"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, nextTick, watch, onMounted, onUnmounted } from 'vue'
import api from '@/services/api'
import { renderizarMarkdown } from '@/utils/markdownSeguro'
import { useAuthStore } from '@/stores/auth'
import { useNavegacao } from '@/composables/useNavegacao'
import { reservarZIndex, liberarZIndex, BASE_ZINDEX } from '@/composables/useZIndex'

const props = defineProps({
  modelValue: Boolean,
  contexto: { type: Object, default: () => ({}) }
})

const emit = defineEmits(['update:modelValue', 'sugestoes-salvas'])

// --- Empilhamento (z-index) via registry central ---
const tokenZIndex = ref(null)
const zIndexReservado = ref(null)
const zIndexEfetivo = computed(() => zIndexReservado.value ?? BASE_ZINDEX)

const authStore = useAuthStore()

const mensagem = ref('')
const historico = ref([])
const isLoading = ref(false)
const isSaving = ref(false)
const finalizado = ref(false)
const chatBloqueado = ref(false)
const sugestoesGeradas = ref([])
const chatContainer = ref(null)
const iaPensando = ref(false)

/** UUID da sessão do chat, gerado uma vez ao abrir o modal.
 *  Todas as mensagens compartilham o mesmo idSessao para agregar custo/tokens. */
const idSessao = ref('')

/** ID da SugestaoMelhoria (rascunho) no modo Tira Dúvidas.
 *  Enviado ao backend a cada mensagem para que a tool AtualizarDuvida atualize
 *  o MESMO rascunho — evitando duplicação de registros e permitindo retomada. */
const idDuvida = ref(null)

/** ID do rascunho da Sugestão (fluxo SIS4), gravado pelo backend no fecho do DESC.
 *  Enviado nas etapas CATEG/PRIOR/CONFIRMAR para atualizar o MESMO registro. */
const idSugestaoRascunho = ref(null)

/** Código do tipo de sugestão escolhido no badge (ex: SUGEST_TIPO1).
 *  Enviado ao backend para classificar o rascunho. */
const tipoSugestaoSelecionado = ref(null)

/** Indica se o chat está em modo Tira Dúvidas */
const modoDuvida = ref(false)

/** Etapa atual do fluxo, controlada pelo backend (Etapa Controller) */
const etapaAtual = ref('')

/** Etapas em que a entrada de texto é desabilitada (apenas badges). */
const etapaSomenteBadges = computed(() => ['TIPO', 'CATEG', 'PRIOR'].includes(etapaAtual.value))

/** Badges sugeridos pelo badge agent no backend */
const badgesOpcoes = ref([])

/** Controla se a bolha informativa de salvamento automático já foi exibida nesta sessão */
const bolhaSalvamentoExibida = ref(false)

/** Estados de sucesso pós-salvamento */
const salvoComSucesso = ref(false)
const contagemRegressiva = ref(0)
let contagemTimer = null

/** Perfil público do usuário logado */
const usuarioLogado = ref(null)
const mostrarBannerPerfil = ref(false)

/** Altura dinâmica do painel para mobile (visualViewport) */
const chatHeight = ref(window.innerHeight - 56)
const isMobile = ref(false)

/** Largura do painel (redimensionável no desktop) */
const LARGURA_MIN = 320
const LARGURA_MAX = 900
const chatWidth = ref(420)

let unlistenViewport = null

// ── Redimensionamento por arrasto (borda esquerda) ──
// O painel é ancorado à direita, então arrastar para a esquerda aumenta a largura.
let resizeStartX = 0
let resizeStartWidth = 0

const iniciarRedimensionamento = (event) => {
  resizeStartX = event.clientX
  resizeStartWidth = chatWidth.value
  document.addEventListener('mousemove', redimensionar)
  document.addEventListener('mouseup', finalizarRedimensionamento)
  document.documentElement.style.userSelect = 'none'
  document.documentElement.style.cursor = 'col-resize'
}

const redimensionar = (event) => {
  const delta = resizeStartX - event.clientX
  const nova = Math.min(LARGURA_MAX, Math.max(LARGURA_MIN, resizeStartWidth + delta))
  chatWidth.value = nova
}

const finalizarRedimensionamento = () => {
  document.removeEventListener('mousemove', redimensionar)
  document.removeEventListener('mouseup', finalizarRedimensionamento)
  document.documentElement.style.userSelect = ''
  document.documentElement.style.cursor = ''
}

const verificarPerfilPublico = async () => {
  try {
    const idUsuario = authStore.idUtilizador
    if (!idUsuario) return
    const response = await api.get(`/Usuarios/${idUsuario}`, { _suppressLoading: true })
    usuarioLogado.value = response.data
    // Mostra banner se não tem apelido ou não aceitou termo
    mostrarBannerPerfil.value = !response.data.apelido || !response.data.aceiteTermoUso
  } catch {
    usuarioLogado.value = null
    mostrarBannerPerfil.value = false
  }
}

const abrirConfigPerfil = () => {
  // Navega para a página de edição do perfil do usuário
  window.location.hash = '#/usuarios/editar/' + authStore.idUtilizador
}

onMounted(async () => {
  isMobile.value = window.innerWidth < 768
  if (isMobile.value && window.visualViewport) {
    const updateHeight = () => {
      chatHeight.value = window.visualViewport.height - 56
    }
    window.visualViewport.addEventListener('resize', updateHeight)
    unlistenViewport = () => window.visualViewport.removeEventListener('resize', updateHeight)
    updateHeight()
  }

  // Verifica perfil público ao abrir
  await verificarPerfilPublico()
})

onUnmounted(() => {
  unlistenViewport?.()
  finalizarRedimensionamento()
  if (contagemTimer) clearInterval(contagemTimer)
  liberarZIndex(tokenZIndex.value)
  tokenZIndex.value = null
  zIndexReservado.value = null
})

const scrollParaBaixo = async () => {
  await nextTick()
  if (chatContainer.value) {
    chatContainer.value.scrollTop = chatContainer.value.scrollHeight
  }
}

/**
 * Posiciona o scroll para que a última mensagem do usuário fique no topo
 * da área visível do chat. Útil quando a resposta do assistente é longa,
 * permitindo que o usuário veja primeiro a própria pergunta.
 */
const scrollUltimaMsgUsuarioParaTopo = async () => {
  await nextTick()
  if (!chatContainer.value) return

  const container = chatContainer.value
  const botoesUsuario = container.querySelectorAll('.chat-bubble-user')
  if (botoesUsuario.length === 0) return

  const ultimo = botoesUsuario[botoesUsuario.length - 1]
  const wrapper = ultimo.closest('.d-flex')
  if (!wrapper) return

  container.scrollTop = wrapper.offsetTop - 8
}

/**
 * Monta o payload base para requisições ao backend.
 * Inclui origem, badgeSelecionado e etapaAtual para o Etapa Controller.
 */
const montarPayload = (msg, origem = 'chat', badgeSelecionado = null) => ({
  mensagem: msg,
  modulo: props.contexto?.modulo || '',
  tela: props.contexto?.tela || '',
  rota: props.contexto?.rota || '',
  versaoSistema: props.contexto?.versaoSistema || '',
  nomeUsuario: props.contexto?.nomeUsuario || '',
  contextoExtra: props.contexto?.contextoExtra || '',
  idDocumentacao: props.contexto?.idDocumentacao || '',
  idUsuario: props.contexto?.idUsuario || null,
  historico: [],
  origem,
  badgeSelecionado,
  etapaAtual: etapaAtual.value,
  idSessao: idSessao.value || null,
  idDuvida: idDuvida.value || null,
  idSugestaoRascunho: idSugestaoRascunho.value || null,
  tipoSugestaoSelecionado: tipoSugestaoSelecionado.value || null
})

const processarResposta = (data) => {
  // Armazena o ID do rascunho no modo Tira Dúvidas para envio nas próximas
  // requisições — o backend atualiza o MESMO registro em vez de criar duplicatas.
  // Persistido em sessionStorage para retomada ao reabrir o modal.
  if (data.idDuvida) {
    idDuvida.value = data.idDuvida
    sessionStorage.setItem('sugestaoDuvidaIdDuvida', String(data.idDuvida))
    // Persiste também o IdSessao: chave única da conversa no backend (SIS4/SIS6).
    // Ao reabrir o modal, a MESMA conversa é retomada com o histórico completo.
    if (idSessao.value) {
      sessionStorage.setItem('sugestaoDuvidaIdSessao', String(idSessao.value))
    }
  }

  // Persiste o ID do rascunho gravado no fecho do DESC (fluxo SIS4).
  if (data.idSugestaoRascunho) {
    idSugestaoRascunho.value = data.idSugestaoRascunho
  }

  etapaAtual.value = data.etapaAtual || ''

  // Detecta modo Tira Dúvidas
  modoDuvida.value = data.etapaAtual === 'DUVIDA'

  // ChatBloqueado: quando NaoConseguiResponder é chamado, o chat é encerrado (estado terminal).
  // Limpa as referências persistidas para NÃO retomar uma dúvida já escalada ao Help Desk.
  if (data.chatBloqueado) {
    chatBloqueado.value = true
    sessionStorage.removeItem('sugestaoDuvidaIdDuvida')
    sessionStorage.removeItem('sugestaoDuvidaIdSessao')
  }

  // Exibe a bolha informativa de salvamento automático apenas na primeira vez
  // em que o modo Dúvida é ativado nesta sessão de chat
  if (modoDuvida.value && !bolhaSalvamentoExibida.value) {
    bolhaSalvamentoExibida.value = true
    historico.value.push({
      role: 'system',
      content: '<div style="font-size:1.1rem;line-height:1.7"> Suas dúvidas são salvas automaticamente e você pode consultá-las depois em: **Menu do usuário (avatar) → Sugestões e Dúvidas** 📋<br><br>🔒 **Fique tranquilo!** Este chat é **100% privado** — nada do que você escrever aqui será exposto ou compartilhado. 😊<br><br>🤗 Fique à vontade para perguntar o que precisar!</div>'
    })
    scrollParaBaixo()
  }

  // Badges sugeridos pelo badge agent
  badgesOpcoes.value = data.badgesSugeridos || []

  if (data.finalizado) {
    finalizado.value = true
    if (data.sugestoesGeradas && data.sugestoesGeradas.length > 0) {
      // Fluxo legado: backend devolveu o array final — persiste via CRUD.
      sugestoesGeradas.value = data.sugestoesGeradas
      historico.value.push({
        role: 'assistant',
        content: data.resposta || 'Sugestões geradas!'
      })
      autoSalvar()
    } else {
      // Novo fluxo SIS4: o backend JÁ persistiu o rascunho no fecho do DESC.
      // Não cria novo registro — apenas confirma a conclusão.
      historico.value.push({
        role: 'assistant',
        content: data.resposta || 'Sugestão registrada com sucesso!'
      })
      exibirSucessoComContagem()
    }
  } else if (data.resposta) {
    historico.value.push({ role: 'assistant', content: data.resposta })
  }
}

const autoSalvar = async () => {
  if (sugestoesGeradas.value.length === 0 || isSaving.value) return
  isSaving.value = true
  try {
    for (const sug of sugestoesGeradas.value) {
      await api.post('/SugestoesMelhoria', {
        titulo: sug.titulo,
        descricao: sug.descricao,
        lstTipoSugestao: sug.lstTipoSugestao,
        lstCategoriaSugestao: sug.lstCategoriaSugestao,
        lstPrioridadeSugestao: sug.lstPrioridadeSugestao,
        modulo: props.contexto?.modulo || '',
        tela: props.contexto?.tela || '',
        rota: props.contexto?.rota || '',
        versaoSistema: props.contexto?.versaoSistema || '',
        idRegistroContexto: props.contexto?.idRegistroContexto || null,
        nomeRegistroContexto: props.contexto?.nomeRegistroContexto || null
      }, { _suppressLoading: true })
    }
    emit('sugestoes-salvas')
    exibirSucessoComContagem()
  } catch (error) {
    console.error('Erro ao salvar:', error)
    historico.value.push({
      role: 'assistant',
      content: 'Ocorreu um erro ao salvar sua sugestão. Tente novamente.'
    })
  } finally {
    isSaving.value = false
  }
}

const exibirSucessoComContagem = () => {
  salvoComSucesso.value = true
  contagemRegressiva.value = 3

  contagemTimer = setInterval(() => {
    contagemRegressiva.value--
    if (contagemRegressiva.value <= 0) {
      clearInterval(contagemTimer)
      contagemTimer = null
      // Auto-abre a página de gestão de sugestões em nova aba
      abrirPaginaSugestoes()
    }
  }, 1000)

  scrollParaBaixo()
}

const abrirPaginaSugestoes = () => {
  useNavegacao().abrirComSession('sugestoesAbrirModulo', 'SugestoesMelhoriaAdministrar')
}

/**
 * Tenta retomar um rascunho de dúvida pendente (modo Tira Dúvidas).
 * Se o backend confirmar via PodeRetomar, restaura o idDuvida e repovoa o
 * histórico persistido — evitando duplicação de rascunhos a cada mensagem.
 * Retorna true se retomou (fluxo de saudação é suprimido), false caso contrário.
 */
const tentarRetomarDuvida = async (idDuvidaPersistido) => {
  iaPensando.value = true
  try {
    // Usa o IdSessao persistido (chave única da conversa) para continuar a MESMA
    // conversa com o histórico completo no backend — em vez de gerar nova chave.
    const idSessaoPersistido = sessionStorage.getItem('sugestaoDuvidaIdSessao')
    if (idSessaoPersistido) {
      idSessao.value = idSessaoPersistido
    }
    const payload = montarPayload('[RETOMAR_DUVIDA]')
    payload.idDuvida = idDuvidaPersistido
    const response = await api.post('/SugestoesMelhoriaAi/retomar-duvida', payload, { _suppressLoading: true })
    const data = response.data || {}

    if (data.podeRetomar && data.idDuvida) {
      idDuvida.value = data.idDuvida
      sessionStorage.setItem('sugestaoDuvidaIdDuvida', String(data.idDuvida))
      if (idSessao.value) {
        sessionStorage.setItem('sugestaoDuvidaIdSessao', String(idSessao.value))
      }
      modoDuvida.value = true
      etapaAtual.value = 'DUVIDA'
      bolhaSalvamentoExibida.value = true

      // Repovoa o histórico com as mensagens persistidas (comentários)
      const mensagens = data.mensagensHistorico || []
      historico.value = mensagens.map(m => ({
        role: m.role || 'user',
        content: m.comentario
      }))

      // Garante uma bolha do assistente indicando a retomada
      if (data.resposta && !historico.value.some(h => h.role === 'assistant')) {
        historico.value.push({ role: 'assistant', content: data.resposta })
      }

      badgesOpcoes.value = data.badgesSugeridos || []
      await scrollParaBaixo()
      return true
    }

    // Rascunho concluído ou não encontrado: remove as referências persistidas
    sessionStorage.removeItem('sugestaoDuvidaIdDuvida')
    sessionStorage.removeItem('sugestaoDuvidaIdSessao')
  } catch {
    // Falha ao retomar: segue o fluxo normal com saudação
    sessionStorage.removeItem('sugestaoDuvidaIdDuvida')
    sessionStorage.removeItem('sugestaoDuvidaIdSessao')
  } finally {
    iaPensando.value = false
  }
  return false
}

const enviarSaudacaoInicial = async () => {
  iaPensando.value = true
  try {
    const payload = montarPayload('[SAUDACAO_INICIAL]')
    const response = await api.post('/SugestoesMelhoriaAi/chat', payload, { _suppressLoading: true })
    const data = response.data
    processarResposta(data)
  } catch (error) {
    const nome = props.contexto?.nomeUsuario || 'usuário'
    const tela = props.contexto?.tela || 'sistema'
    historico.value.push({
      role: 'assistant',
      content: `Olá, ${nome}! Seja bem-vindo(a) ao assistente de sugestões. Estou vendo que você está na tela de <strong>${tela}</strong>. Como posso ajudar? 😊`
    })
  } finally {
    iaPensando.value = false
    await scrollParaBaixo()
  }
}

const enviarMensagem = async () => {
  if (!mensagem.value.trim() || isLoading.value) return

  // Remove badges imediatamente quando o usuário digita
  badgesOpcoes.value = []

  const msg = mensagem.value
  mensagem.value = ''

  historico.value.push({ role: 'user', content: msg })
  await scrollParaBaixo()

  isLoading.value = true
  iaPensando.value = true
  try {
    const payload = montarPayload(msg, 'chat', null)

    // O backend acumula a conversa por IdSessao nos DOIS fluxos (SIS4 e SIS6)
    // via ConversaSessionStore — o frontend envia sempre historico: [] (incremental).
    // O montarPayload já define historico: [], portanto não há reenvio integral.

    const response = await api.post('/SugestoesMelhoriaAi/chat', payload, { _suppressLoading: true })
    const data = response.data
    processarResposta(data)
  } catch (error) {
    const msgErro = error.response?.data?.message || 'Erro ao processar. Verifique a configuração de IA.'
    historico.value.push({ role: 'assistant', content: msgErro })
  } finally {
    isLoading.value = false
    iaPensando.value = false
    await scrollUltimaMsgUsuarioParaTopo()
  }
}

/**
 * Usuário clicou em um badge.
 * Envia a seleção ao backend com origem="badge" para o Etapa Controller processar.
 */
const selecionarOpcao = async (value) => {
  if (isLoading.value) return

  // Busca o texto amigável do badge
  const opcao = badgesOpcoes.value.find(o => o.value === value)
  const textoBadge = opcao ? opcao.text : value

  // Registra o tipo escolhido no badge para classificar o rascunho no backend.
  if (etapaAtual.value === 'TIPO') {
    tipoSugestaoSelecionado.value = value
  }

  // Remove as badges imediatamente para evitar clique duplicado
  badgesOpcoes.value = []

  historico.value.push({ role: 'user', content: textoBadge })
  await scrollParaBaixo()

  isLoading.value = true
  iaPensando.value = true
  try {
    const payload = montarPayload(textoBadge, 'badge', value)

    // O backend acumula a conversa por IdSessao nos DOIS fluxos (SIS4 e SIS6)
    // via ConversaSessionStore — o frontend envia sempre historico: [] (incremental).
    // O montarPayload já define historico: [], portanto não há reenvio integral.

    const response = await api.post('/SugestoesMelhoriaAi/chat', payload, { _suppressLoading: true })
    const data = response.data
    processarResposta(data)
  } catch (error) {
    const msgErro = error.response?.data?.message || 'Erro ao processar. Verifique a configuração de IA.'
    historico.value.push({ role: 'assistant', content: msgErro })
  } finally {
    isLoading.value = false
    iaPensando.value = false
    await scrollUltimaMsgUsuarioParaTopo()
  }
}

const reiniciar = () => {
  historico.value = []
  sugestoesGeradas.value = []
  finalizado.value = false
  mensagem.value = ''
  modoDuvida.value = false
  idDuvida.value = null
  idSugestaoRascunho.value = null
  tipoSugestaoSelecionado.value = null
  etapaAtual.value = ''
  badgesOpcoes.value = []
  bolhaSalvamentoExibida.value = false
  salvoComSucesso.value = false
  contagemRegressiva.value = 0
  // Se houver um IdSessao persistido (retomada de dúvida), reutiliza a MESMA
  // chave da conversa — caso contrário gera um UUID único para esta sessão.
  // A reabertura do modal com retomada preserva todo o histórico no backend.
  const idSessaoPersistido = sessionStorage.getItem('sugestaoDuvidaIdSessao')
  idSessao.value = idSessaoPersistido || crypto.randomUUID()
  if (contagemTimer) {
    clearInterval(contagemTimer)
    contagemTimer = null
  }
}

const fechar = () => {
  reiniciar()
  emit('update:modelValue', false)
}

watch(() => props.modelValue, async (val) => {
  if (val) {
    const { token, zIndex: z } = reservarZIndex()
    tokenZIndex.value = token
    zIndexReservado.value = z
    reiniciar()
    // Se existir um rascunho de dúvida pendente (sessionStorage), tenta retomar
    // a conversa antes de enviar a saudação inicial.
    // O reiniciar() já reutiliza o IdSessao persistido como chave da conversa;
    // o tentarRetomarDuvida garante o repovoamento do histórico persistido.
    const idPersistido = sessionStorage.getItem('sugestaoDuvidaIdDuvida')
    if (idPersistido && !Number.isNaN(Number(idPersistido))) {
      const retomado = await tentarRetomarDuvida(Number(idPersistido))
      if (retomado) return
      // Falhou a retomada: descarta referências persistidas e inicia nova sessão
      sessionStorage.removeItem('sugestaoDuvidaIdSessao')
    }
    enviarSaudacaoInicial()
  } else {
    liberarZIndex(tokenZIndex.value)
    tokenZIndex.value = null
    zIndexReservado.value = null
  }
})
</script>

<style scoped>
.chat-panel-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: flex-end;
}

.chat-panel {
  width: var(--chat-width, 420px);
  max-width: 100vw;
  height: var(--chat-height, calc(100vh - 56px));
  top: 56px;
  position: relative;
  background: var(--bs-body-bg);
  display: flex;
  flex-direction: column;
  animation: slideIn 0.2s ease-out;
}

/* Alça de redimensionamento na borda esquerda (desktop) */
.chat-resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  width: 5px;
  height: 100%;
  cursor: col-resize;
  background: transparent;
  transition: background 0.15s;
  z-index: 10;
}

.chat-resize-handle:hover,
.chat-resize-handle:active {
  background: var(--bs-primary);
  opacity: 0.4;
}

@keyframes slideIn {
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
}

.chat-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  /* Identidade visual alinhada ao header global (MainDashboard .admin-header-global) */
  background-color: #34495E;
  border-bottom: 1px solid #1f2d3a;
  color: #E6EAEE;
  flex-shrink: 0;
}

.chat-panel-header h6 {
  color: #FFFFFF;
}

/* Ícone do robô (Helper) na cor de acento da IA, como no header global */
.chat-panel-header .bi-robot {
  color: #22D3EE;
}

/* Badge "AI" — mesma identidade do header global (.header-action-badge-ai) */
.chat-header-badge-ai {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 1.2rem;
  padding: 0 0.45rem;
  font-size: 0.62rem;
  font-weight: 700;
  line-height: 1;
  border: none;
  border-radius: 0.35rem;
  background-color: #22D3EE;
  color: #083344;
  vertical-align: middle;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.chat-bubble {
  max-width: 85%;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 1.2rem;
  line-height: 1.6;
  white-space: pre-wrap;
}

.chat-bubble :deep(p) {
  margin: 0 0 1px 0;
}

.chat-bubble :deep(p:last-child),
.chat-bubble :deep(p:only-child) {
  margin-bottom: 0;
}

.chat-bubble-user {
  background: var(--bs-primary);
  color: white;
  border-bottom-right-radius: 4px;
}

.chat-bubble-assistant {
  background: var(--bs-tertiary-bg);
  border-bottom-left-radius: 4px;
}

.chat-bubble-system {
  background: var(--bs-info-bg-subtle);
  color: var(--bs-emphasis-color);
  border: 1px dashed var(--bs-info-border-subtle);
  font-size: 0.85rem;
  border-bottom-left-radius: 4px;
}

.chat-profile-banner {
  padding: 8px 16px;
  background: #FEF3C7;
  color: #92400E;
  font-size: 0.8rem;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #FDE68A;
}

[data-bs-theme="dark"] .chat-profile-banner {
  background: #78350F;
  color: #FCD34D;
  border-color: #92400E;
}

.chat-profile-banner a {
  color: inherit;
  font-weight: 600;
  text-decoration: underline;
}

.chat-panel-footer {
  padding: 12px 16px;
  border-top: 1px solid var(--bs-border-color);
  flex-shrink: 0;
  background: var(--bs-body-bg);
  scroll-margin-bottom: 16px;
}

.chat-input {
  resize: vertical;
  min-height: 42px;
  max-height: 150px;
  font-size: 1.2rem;
  line-height: 1.6;
}

.btn-send {
  height: 42px;
  flex-shrink: 0;
}

.input-area {
  display: flex;
  gap: 8px;
  align-items: flex-end;
}

.chat-input {
  resize: vertical;
  min-height: 42px;
  max-height: 150px;
  font-size: 0.95rem;
  line-height: 1.5;
}

.btn-send {
  height: 42px;
  flex-shrink: 0;
}

.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 4px 0;
}

.typing-indicator span {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--bs-secondary-color);
  animation: typing 1.4s infinite ease-in-out;
}

.typing-indicator span:nth-child(1) { animation-delay: 0s; }
.typing-indicator span:nth-child(2) { animation-delay: 0.2s; }
.typing-indicator span:nth-child(3) { animation-delay: 0.4s; }

@keyframes typing {
  0%, 60%, 100% { opacity: 0.3; transform: scale(0.8); }
  30% { opacity: 1; transform: scale(1.2); }
}

.badges-container {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.badge-opcao {
  cursor: pointer;
  font-size: 0.8rem;
  padding: 6px 12px;
  border: 1px solid var(--bs-primary-border-subtle);
  background: var(--bs-tertiary-bg);
  color: var(--bs-body-color);
  border-radius: 20px;
  transition: all 0.15s ease;
  user-select: none;
}

.badge-opcao:hover {
  background: var(--bs-primary);
  color: white;
  border-color: var(--bs-primary);
}

/* Aumento de fontes para mobile ( < 768px ) */
@media (max-width: 767.98px) {
  .chat-panel {
    width: 100vw;
  }

  .chat-bubble {
    font-size: 1.4rem;
    padding: 8px 14px;
  }

  .chat-input {
    font-size: 1.2rem;
    min-height: 52px;
  }

  .btn-send {
    height: 52px;
    width: 52px;
  }

  .badge-opcao {
    font-size: 1.1rem;
    padding: 10px 18px;
  }

  .chat-panel-header h6 {
    font-size: 1.2rem;
  }

  .chat-panel-footer {
    padding: 16px;
  }

  .badges-container {
    gap: 8px;
  }
}

.badge-admin-interno-chat {
  font-size: 0.6rem;
  padding: 0.15rem 0.4rem;
  border-radius: 999px;
  background: rgba(255,255,255,0.2);
  color: white;
  font-weight: 600;
  vertical-align: middle;
}
</style>
